import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: form.password });
      toast.success("Password updated — sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-5 py-16 hero-grain relative" data-testid="reset-password-page">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--brand)]/10 blur-3xl pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-3 justify-center mb-8 group" data-testid="reset-brand-link">
          <img src="/logo.png" alt="Voyage Switzerland" className="h-20 w-auto" />
        </Link>

        <div className="rounded-[2rem] bg-white border border-slate-100 shadow-[0_30px_70px_-30px_rgba(11,19,43,0.25)] p-7 sm:p-9">
          {token ? (
            <>
              <div className="mb-7 text-center">
                <h2 className="font-display font-bold text-xl text-[#0b132b]" data-testid="reset-title">Choose a new password</h2>
                <p className="mt-1.5 text-sm text-slate-500">Your reset link is valid for 1 hour.</p>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                <div className="relative">
                  <input
                    data-testid="reset-password-input"
                    type={showPw ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="New password"
                    className={`${inputCls} pr-12`}
                  />
                  <button
                    type="button"
                    data-testid="reset-toggle-password-button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[var(--brand)] transition-colors"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                  </button>
                </div>
                <input
                  data-testid="reset-confirm-input"
                  type={showPw ? "text" : "password"}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  placeholder="Confirm new password"
                  className={inputCls}
                />
                {error && (
                  <p className="text-sm font-semibold text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="reset-error-message">
                    {error}
                  </p>
                )}
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
                >
                  {busy ? "Updating…" : "Update Password"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-invalid-link">
              <h2 className="font-display font-bold text-xl text-[#0b132b]">Invalid reset link</h2>
              <p className="mt-2 text-sm text-slate-500">This link is missing its token. Request a fresh reset link.</p>
              <Link
                to="/login"
                data-testid="reset-go-login-link"
                className="mt-6 inline-flex items-center rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
              >
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
